import { Check, ArrowRight } from "lucide-react";

const TIERS = [
  {
    name: "Starter",
    price: 1000,
    funderCount: 1,
    tagline: "For limited liability companies & early-stage founders",
    bullets: [
      "1 grant or funding application",
      "Core 5-year financial model",
      "Investor one-pager",
      "Email support throughout",
    ],
    cta: "Choose 1 funder",
    highlight: false,
  },
  {
    name: "Growth",
    price: 1500,
    funderCount: 2,
    tagline: "For revenue-generating SMEs ready to scale",
    bullets: [
      "1 grant + 1 VC application",
      "Full investor data room build",
      "VC-grade financial model + cap table",
      "Pitch deck (10-slide)",
      "Priority support + 2 review rounds",
    ],
    cta: "Choose 2 funders",
    highlight: true,
  },
  {
    name: "Scale",
    price: 3000,
    funderCount: 3,
    tagline: "For Series-A ready and larger businesses",
    bullets: [
      "Up to 3 applications (grants or VC)",
      "Comprehensive data room + diligence prep",
      "Custom financial model + sensitivity scenarios",
      "Full investor pitch deck + narrative",
      "Investor follow-up & Q&A support",
      "Dedicated lead analyst",
    ],
    cta: "Choose 3 funders",
    highlight: false,
  },
];

function selectAndScroll(count) {
  window.dispatchEvent(
    new CustomEvent("seedtech:preset-funders", { detail: { count } }),
  );
  const el = document.getElementById("funders");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function Pricing() {
  return (
    <section id="pricing" className="bg-white py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 sm:px-12">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-terracotta">Pricing</p>
          <h2 className="mt-3 font-display text-4xl font-semibold text-umber sm:text-5xl">
            Transparent fees per application.
          </h2>
          <p className="mt-5 text-lg text-umber/70">
            Pick a plan — we&apos;ll auto-select matching funders below. Want more than 3? Pick up to 7 in
            the Funder Marketplace and unlock a 10% bundle discount automatically.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {TIERS.map((t) => (
            <article
              key={t.name}
              data-testid={`pricing-tier-${t.name.toLowerCase()}`}
              className={`relative flex flex-col rounded-2xl border p-8 transition hover:-translate-y-1 ${
                t.highlight
                  ? "border-terracotta bg-umber text-cream shadow-2xl shadow-umber/20 lg:scale-[1.03]"
                  : "border-border bg-sand/40 text-umber hover:shadow-lg"
              }`}
            >
              {t.highlight && (
                <span className="absolute -top-3 left-8 rounded-full bg-gold px-3 py-1 text-[10px] font-medium uppercase tracking-wider text-umber">
                  Most popular
                </span>
              )}
              <h3 className={`font-display text-2xl font-semibold ${t.highlight ? "text-cream" : "text-umber"}`}>
                {t.name}
              </h3>
              <p className={`mt-1 text-sm ${t.highlight ? "text-cream/70" : "text-umber/65"}`}>{t.tagline}</p>

              <div className="mt-6 flex items-end gap-1">
                <span className={`font-display text-6xl font-semibold ${t.highlight ? "text-cream" : "text-umber"}`}>
                  ${t.price.toLocaleString()}
                </span>
                <span className={`mb-2 text-sm ${t.highlight ? "text-cream/60" : "text-umber/55"}`}>
                  / {t.funderCount} platform{t.funderCount > 1 ? "s" : ""}
                </span>
              </div>

              <ul className="mt-8 space-y-3">
                {t.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm">
                    <Check
                      className={`mt-0.5 h-4 w-4 flex-shrink-0 ${t.highlight ? "text-gold" : "text-terracotta"}`}
                    />
                    <span className={t.highlight ? "text-cream/85" : "text-umber/80"}>{b}</span>
                  </li>
                ))}
              </ul>

              <button
                data-testid={`pricing-cta-${t.name.toLowerCase()}`}
                onClick={() => selectAndScroll(t.funderCount)}
                className={`mt-10 inline-flex items-center justify-center gap-2 rounded-md px-5 py-3 text-sm font-medium transition hover:-translate-y-0.5 ${
                  t.highlight
                    ? "bg-terracotta text-cream hover:bg-terracotta-dark"
                    : "bg-umber text-cream hover:bg-umber-dark"
                }`}
              >
                {t.cta}
                <ArrowRight className="h-4 w-4" />
              </button>
            </article>
          ))}
        </div>

        <p className="mx-auto mt-10 max-w-2xl text-center text-sm text-umber/60">
          Or build a custom bundle of up to 7 funders below — pick 4 or more for 10% off automatically.
        </p>
      </div>
    </section>
  );
}
